import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Category as CategoryIcon,
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import apiClient from '../lib/api';
import { toast } from 'react-hot-toast';

/**
 * Taxonomy management (BR-CAT-09a): view / create / edit / retire categories. Any active-set change
 * bumps the taxonomy version (enforced server-side); we surface the returned version so an admin can
 * confirm the bump. Admin-only — backed by the gated /api/admin/taxonomy endpoints.
 */

const emptyForm = {
  id: '',
  name: '',
  description: '',
  synonyms: '',
  order: 0,
  active: true,
  sensitive: false,
};

const toForm = (category) => ({
  id: category.id || '',
  name: category.name || '',
  description: category.description || '',
  synonyms: Array.isArray(category.synonyms) ? category.synonyms.join(', ') : '',
  order: typeof category.order === 'number' ? category.order : 0,
  active: category.active !== false,
  sensitive: category.sensitive === true,
});

const fromForm = (form) => ({
  id: form.id.trim(),
  name: form.name.trim(),
  description: form.description,
  synonyms: form.synonyms
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean),
  order: Number(form.order) || 0,
  active: form.active,
  sensitive: form.sensitive,
});

const TaxonomyManager = () => {
  const [categories, setCategories] = useState([]);
  const [version, setVersion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.getAdminTaxonomy();
      const data = res?.data || {};
      setCategories(Array.isArray(data.categories) ? data.categories : []);
      setVersion(typeof data.version === 'number' ? data.version : null);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to load taxonomy');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(false);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (category) => {
    setEditing(true);
    setForm(toForm(category));
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (!saving) setDialogOpen(false);
  };

  const handleField = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    const payload = fromForm(form);
    if (!payload.id || !payload.name) {
      toast.error('id and name are required');
      return;
    }
    setSaving(true);
    try {
      const res = editing
        ? await apiClient.updateTaxonomyCategory(payload.id, payload)
        : await apiClient.createTaxonomyCategory(payload);
      const newVersion = res?.data?.version;
      toast.success(
        `${editing ? 'Updated' : 'Created'} “${payload.name}”${
          typeof newVersion === 'number' ? ` · taxonomy v${newVersion}` : ''
        }`
      );
      setDialogOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editing) return;
    if (
      !window.confirm(
        `Delete “${form.name}”? Cards already classified keep their data; the category is removed from the active taxonomy.`
      )
    ) {
      return;
    }
    setSaving(true);
    try {
      const res = await apiClient.retireTaxonomyCategory(form.id);
      const newVersion = res?.data?.version;
      toast.success(
        `Deleted “${form.name}”${typeof newVersion === 'number' ? ` · taxonomy v${newVersion}` : ''}`
      );
      setDialogOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CategoryIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
              Card Taxonomy
            </Typography>
            {version !== null && (
              <Chip label={`v${version}`} size="small" sx={{ ml: 1 }} aria-label="taxonomy version" />
            )}
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New category
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The category list browse rails are built from. Editing, creating, or deleting a category
          bumps the taxonomy version; cards classified against an older version are re-scanned by the
          backfill run.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Synonyms</TableCell>
                  <TableCell align="right">Order</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No categories yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  categories.map((category) => (
                    <TableRow
                      key={category.id}
                      hover
                      onClick={() => openEdit(category)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {category.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {category.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {(category.synonyms || []).join(', ')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{category.order}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={category.active !== false ? 'Active' : 'Retired'}
                          color={category.active !== false ? 'success' : 'default'}
                          variant={category.active !== false ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Edit category' : 'New category'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="ID (slug)"
              value={form.id}
              onChange={handleField('id')}
              disabled={editing}
              helperText={editing ? 'The id cannot be changed.' : 'Lowercase, hyphenated, e.g. faith-hope'}
              fullWidth
              inputProps={{ 'aria-label': 'category id' }}
            />
            <TextField
              label="Name"
              value={form.name}
              onChange={handleField('name')}
              fullWidth
              inputProps={{ 'aria-label': 'category name' }}
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={handleField('description')}
              fullWidth
              multiline
              minRows={2}
              helperText="The classifier reads this to decide where an image belongs."
            />
            <TextField
              label="Synonyms (comma-separated)"
              value={form.synonyms}
              onChange={handleField('synonyms')}
              fullWidth
            />
            <TextField
              label="Order"
              type="number"
              value={form.order}
              onChange={handleField('order')}
              sx={{ maxWidth: 160 }}
            />
            <FormControlLabel
              control={<Switch checked={form.active} onChange={handleField('active')} />}
              label="Active"
            />
            <FormControlLabel
              control={<Switch checked={form.sensitive} onChange={handleField('sensitive')} />}
              label="Sensitive (stricter apply floor for direct-loss categories)"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: editing ? 'space-between' : 'flex-end', px: 3, pb: 2 }}>
          {editing && (
            <Button
              onClick={handleDelete}
              color="error"
              startIcon={<DeleteIcon />}
              disabled={saving}
            >
              Delete
            </Button>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={closeDialog} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} variant="contained" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default TaxonomyManager;
